// Marketing event tracking helper.
//
// Emits a single semantic event to the THREE pixels that already live on the
// site (gated by Consent Mode v2 — calls are no-ops until the user clicks
// "Akceptuj" in the cookie banner):
//
//   - Meta Pixel (fbq) — standard event names (`Lead`, `Subscribe`, …)
//   - Google Ads (gtag) — `conversion` events (only when a Conversion Label
//     is configured via REACT_APP_GOOGLE_ADS_LEAD_LABEL etc.)
//   - GA4 (gtag) — generic `event` calls so reports also see them
//
// IMPORTANT: this file MUST stay free of side effects on import. We never
// load pixels here; we only call into already-loaded `fbq` / `gtag`.

const GOOGLE_ADS_ID = process.env.REACT_APP_GOOGLE_ADS_ID; // e.g. "AW-1031240000"

const ADS_LABELS = {
  lead: process.env.REACT_APP_GOOGLE_ADS_LEAD_LABEL,
  subscribe: process.env.REACT_APP_GOOGLE_ADS_SUBSCRIBE_LABEL,
  view_content: process.env.REACT_APP_GOOGLE_ADS_VIEW_CONTENT_LABEL,
};

// Default monetary value (PLN) for a soft Lead — algorithms learn faster when
// events carry a stable, non-zero value. Override per-call when known.
const DEFAULT_LEAD_VALUE_PLN = 600;

function fireMeta(eventName, params = {}) {
  if (typeof window === "undefined" || typeof window.fbq !== "function") return;
  try {
    window.fbq("track", eventName, params);
  } catch (e) {
    /* swallow */
  }
}

function fireGtag(eventName, params = {}) {
  if (typeof window === "undefined" || typeof window.gtag !== "function") return;
  try {
    window.gtag("event", eventName, params);
  } catch (e) {
    /* swallow */
  }
}

function fireGoogleAdsConversion(labelKey, params = {}) {
  const label = ADS_LABELS[labelKey];
  if (!GOOGLE_ADS_ID || !label) return; // graceful no-op until configured
  if (typeof window === "undefined" || typeof window.gtag !== "function") return;
  try {
    window.gtag("event", "conversion", {
      send_to: `${GOOGLE_ADS_ID}/${label}`,
      ...params,
    });
  } catch (e) {
    /* swallow */
  }
}

/**
 * `trackLead` — fired when the user expresses strong intent (clicked
 * "Zapisz się" / went to the booking flow / opened a specific trip slug).
 */
export function trackLead({ value = DEFAULT_LEAD_VALUE_PLN, currency = "PLN", contentName, contentId } = {}) {
  fireMeta("Lead", {
    value,
    currency,
    ...(contentName ? { content_name: contentName } : {}),
    ...(contentId ? { content_ids: [contentId], content_type: "trip" } : {}),
  });
  fireGoogleAdsConversion("lead", {
    value,
    currency,
    ...(contentId ? { transaction_id: contentId } : {}),
  });
  fireGtag("generate_lead", {
    value,
    currency,
    ...(contentName ? { item_name: contentName } : {}),
    ...(contentId ? { item_id: contentId } : {}),
  });
}

/**
 * `trackSubscribe` — newsletter signup completed successfully.
 */
export function trackSubscribe({ value = 0, currency = "PLN" } = {}) {
  fireMeta("Subscribe", { value, currency });
  fireGoogleAdsConversion("subscribe", { value, currency });
  fireGtag("sign_up", { method: "newsletter" });
}

/**
 * `trackViewContent` — soft signal: user reached the pricing/dates section
 * or opened a specific trip card (low intent, useful for retargeting pools).
 */
export function trackViewContent({ contentName, contentId, value = 0, currency = "PLN" } = {}) {
  fireMeta("ViewContent", {
    value,
    currency,
    ...(contentName ? { content_name: contentName } : {}),
    ...(contentId ? { content_ids: [contentId], content_type: "trip" } : {}),
  });
  fireGoogleAdsConversion("view_content", { value, currency });
  fireGtag("view_item", {
    currency,
    value,
    ...(contentName ? { item_name: contentName } : {}),
    ...(contentId ? { item_id: contentId } : {}),
  });
}

/**
 * `trackContact` — user clicked the phone or email link in the footer/sticky.
 */
export function trackContact(channel /* "phone" | "email" */) {
  fireMeta("Contact", { method: channel });
  fireGtag("contact", { method: channel });
}
